import { PackagePlus } from 'lucide-react'
import { useState } from 'react'

import { Button, ModalFrame } from '@krp/ui-kit'

import { InputField } from '../../../shared/ui/InputField'
import { parsePrice, priceProblem } from '../model/input'
import type { CreateRequest, InventoryVm } from '../model/types'

/**
 * Создание товара по строке «не найден» (карточки 100/101): наименование,
 * ОТСКАНИРОВАННЫЙ штрихкод (не правится) и цены складов этой базы. Диалог, а
 * не окно реестра: он возвращает значение вызывающему и по F5 не воскресает.
 * Итог команды показывает строка, а не диалог: окно закрывается сразу.
 */
export function CreateProductDialog({
  rowId,
  barcode,
  warehouses,
  onCreate,
  onClose,
}: {
  rowId: string
  barcode: string
  warehouses: InventoryVm['createWarehouses']
  onCreate: (request: CreateRequest) => void
  onClose: () => void
}) {
  const [name, setName] = useState('')
  const [prices, setPrices] = useState<Record<string, string>>({})
  const [tried, setTried] = useState(false)

  const priced = warehouses.filter((warehouse) => warehouse.hasRetailPriceType)
  const problems = priced.flatMap((warehouse) => {
    const problem = priceProblem((prices[warehouse.refKey] ?? '').trim(), warehouse.name)
    return problem === null ? [] : [problem]
  })
  const nameMissing = name.trim() === ''

  const submit = () => {
    setTried(true)
    if (nameMissing || problems.length > 0) return
    // Пустая цена и ноль — «не задана»: в 1С уходит только то, что человек ввёл.
    const list = priced.flatMap((warehouse) => {
      const value = parsePrice((prices[warehouse.refKey] ?? '').trim())
      return value === null ? [] : [{ warehouseRefKey: warehouse.refKey, price: value }]
    })
    onCreate({ rowId, name: name.trim(), prices: list })
  }

  return (
    <ModalFrame title="Создать товар в 1С" size="md" covered={false} onClose={onClose}>
      <div className="onec-form">
        <InputField
          label="Наименование"
          value={name}
          onChange={setName}
          onEnter={submit}
          autoFocus
          note={tried && nameMissing ? 'Укажите наименование товара' : null}
        />
        <p>
          Штрихкод: <b>{barcode}</b> — пишется в 1С как есть, товар сразу сканируется и на кассе.
        </p>
        <fieldset className="onec-section">
          <legend>Цены складов</legend>
          {warehouses.length === 0 && <p>Справочник складов пуст — цену задать некуда.</p>}
          {warehouses.map((warehouse) =>
            warehouse.hasRetailPriceType ? (
              <InputField
                key={warehouse.refKey}
                label={`Цена, ${warehouse.name}`}
                inputMode="text"
                value={prices[warehouse.refKey] ?? ''}
                onChange={(value) => setPrices((current) => ({ ...current, [warehouse.refKey]: value }))}
                onEnter={submit}
              />
            ) : (
              // Без розничного вида цен цену писать некуда: не даём ввести, чтобы она не пропала молча.
              <p key={warehouse.refKey}>
                {warehouse.name}: <b>нет розничного вида цен</b> — цену задать нельзя
              </p>
            ),
          )}
        </fieldset>
        <p>Единицу не задаём; количество укажете в строке обхода.</p>
        {tried && problems.length > 0 && (
          <ul role="alert">
            {problems.map((problem) => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        )}
        <div className="onec-actions">
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" icon={<PackagePlus size={16} aria-hidden="true" />} onClick={submit}>
            Создать
          </Button>
        </div>
      </div>
    </ModalFrame>
  )
}
